import {
	ContextMenu,
	ContextMenuDeleteItem,
	IDataListColumn,
	IconButton,
	Id,
	PaginatedAutoDataList,
	RoleIndexResource,
	Trigger,
	isSuccess,
	laravelPaginatedListFetchConfig,
	roleService,
	useToasts,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { createUserRolesService } from "../shared/api/userService";
import { UserRolesAttachDrawer } from "../users/UserRolesAttachDrawer";

export const UserRolesList: FC = () => {
	const { userId = "" } = useParams();
	const { t } = useTranslation();
	const service = useMemo(() => createUserRolesService(userId), [userId]);
	const { showToast } = useToasts();
	const queryClient = useQueryClient();

	const onDelete = useCallback(
		async (id: Id) => {
			const res = await service.detachRoles([id]);
			if (isSuccess(res)) {
				showToast({
					type: "success",
					title: t("User Roles"),
					body: t("Role was successfully removed from the user."),
				});
				await queryClient.invalidateQueries({ queryKey: service.listKey });
				await queryClient.invalidateQueries({ queryKey: service.roleOptionsByUserKey });
				await queryClient.resetQueries({ queryKey: roleService.indexKey });
			}
		},
		[queryClient, service, showToast, t],
	);

	const columns: IDataListColumn<RoleIndexResource>[] = useMemo(
		() => [
			{
				heading: t("Name"),
				valueKey: "name",
				type: "value",
			},
			{
				heading: "",
				size: "max-content",
				type: "actions",
				action: ({ id }) => (
					<ContextMenu displayShortcutsAs="icon">
						<ContextMenuDeleteItem
							label={t("Remove role")}
							title={t("Remove role")}
							heading={t("Close")}
							labels={{ close: t("Close"), confirm: t("Confirm") }}
							action={() => onDelete(id)}
						>
							{t("Are you sure you want to remove this role from the user?")}
						</ContextMenuDeleteItem>
					</ContextMenu>
				),
			},
		],
		[onDelete, t],
	);

	return (
		<PaginatedAutoDataList
			name="userRoles"
			heading={t("Roles")}
			empty={t("No role attached")}
			columns={columns}
			listFetchConfig={laravelPaginatedListFetchConfig(service.listKey, service.index)}
			headerActions={
				<Trigger render={(props) => <UserRolesAttachDrawer {...props} userId={userId} />}>
					{(props) => <IconButton {...props} icon="plus" title={t("Add")} color="primary" size="small" />}
				</Trigger>
			}
		/>
	);
};
